import { useEffect, useMemo, useState, Fragment } from "react";
import { Download, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

function fmtWeek(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Whole-dollar currency, e.g. $53,798 */
function Money({ value }) {
  const neg = value < 0;
  const abs = Math.abs(Math.round(value || 0));
  return (
    <span className="font-mono-bm tabular-nums whitespace-nowrap">
      {neg ? "−" : ""}${abs.toLocaleString()}
    </span>
  );
}

export default function MondayReport() {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState({});

  function toggle(cat) {
    setOpen((o) => ({ ...o, [cat]: !o[cat] }));
  }

  useEffect(() => {
    api.get("/reports/monday").then((r) => setDoc(r.data)).catch(() => setErr(true));
  }, []);

  const csv = useMemo(() => {
    if (!doc) return "";
    const wk = doc.weeks.map((w) => `${fmtWeek(w.start)} ${w.year}`);
    const lines = [];
    lines.push("Report 1 — Revenue by store (last 3 completed weeks)");
    lines.push(["Store", ...wk].join(","));
    doc.revenue.stores.forEach((s) =>
      lines.push([s.name, ...s.values.map((v) => Math.round(v))].join(","))
    );
    lines.push(["Totals", ...doc.revenue.totals.map((v) => Math.round(v))].join(","));
    lines.push("");
    lines.push("Report 2 — Units by category (last 3 completed weeks)");
    lines.push(["Category", ...wk].join(","));
    doc.categories.rows.forEach((r) => {
      lines.push([`"${r.category}"`, ...r.values].join(","));
      (r.children || []).forEach((c) =>
        lines.push([`"  ${c.category}"`, ...c.values].join(","))
      );
    });
    return lines.join("\n");
  }, [doc]);

  function download() {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monday-report-${doc.weeks[doc.weeks.length - 1].start}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (err) return <div className="bm-eyebrow text-[#c72c2c]" data-testid="monday-report-error">Could not load the Monday report.</div>;
  if (!doc) return <div className="bm-eyebrow text-neutral-500" data-testid="monday-report-loading">Loading…</div>;

  const weeks = doc.weeks;
  const years = [...new Set(weeks.map((w) => w.year))];

  return (
    <div className="space-y-8" data-testid="monday-report">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="bm-eyebrow text-neutral-500">
          Last 3 completed weeks · as of {fmtWeek(doc.generated_at.slice(0, 10))} · all four stores
        </div>
        <button
          onClick={download}
          data-testid="monday-download-button"
          className="bm-eyebrow px-3 py-2 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#f5f2eb] flex items-center gap-2"
        >
          <Download size={13} /> Download CSV
        </button>
      </div>

      {/* Report 1 — Revenue by store */}
      <section className="border-2 border-[#0a0a0a] bg-[#faf8f2] overflow-hidden" data-testid="report-revenue">
        <div className="px-4 py-3 border-b border-[#0a0a0a] bg-[#ede8dc]">
          <div className="bm-eyebrow text-[#c72c2c]">Report 1</div>
          <h3 className="bm-headline text-xl">Revenue by store</h3>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#ede8dc]">
              <th rowSpan={2} className="text-left align-bottom px-2 sm:px-4 py-2 bm-eyebrow text-neutral-600 border-b border-[#0a0a0a]/20">Store</th>
              {years.map((y) => (
                <th key={y} colSpan={weeks.filter((w) => w.year === y).length} className="text-center px-3 py-1.5 bm-eyebrow text-neutral-600 border-b border-[#0a0a0a]/20">
                  {y}
                </th>
              ))}
            </tr>
            <tr className="bg-[#ede8dc]">
              {weeks.map((w) => (
                <th key={w.start} className="text-right px-2 sm:px-4 py-2 bm-eyebrow text-neutral-700 border-b border-[#0a0a0a]/30 whitespace-nowrap">
                  {fmtWeek(w.start)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doc.revenue.stores.map((s, i) => (
              <tr key={s.name} className={i % 2 ? "bg-black/[0.03]" : ""} data-testid={`revenue-row-${i}`}>
                <td className="text-left px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium">{s.name}</td>
                {s.values.map((v, j) => (
                  <td key={j} className="text-right px-2 sm:px-4 py-3 text-xs sm:text-sm"><Money value={v} /></td>
                ))}
              </tr>
            ))}
            <tr className="border-t-2 border-[#0a0a0a] bg-[#ede8dc]" data-testid="revenue-totals">
              <td className="text-left px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold">Totals</td>
              {doc.revenue.totals.map((v, j) => (
                <td key={j} className="text-right px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold"><Money value={v} /></td>
              ))}
            </tr>
          </tbody>
        </table>
      </section>

      {/* Report 2 — Units by category */}
      <section className="border-2 border-[#0a0a0a] bg-[#faf8f2] overflow-hidden" data-testid="report-categories">
        <div className="px-4 py-3 border-b border-[#0a0a0a] bg-[#ede8dc]">
          <div className="bm-eyebrow text-[#c72c2c]">Report 2</div>
          <h3 className="bm-headline text-xl">Units by category</h3>
          <div className="bm-eyebrow text-neutral-500 normal-case tracking-normal mt-0.5">Ranked by most recent week's revenue · tap a category to see sub-categories</div>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#ede8dc]">
              <th rowSpan={2} className="text-left align-bottom px-2 sm:px-4 py-2 bm-eyebrow text-neutral-600 border-b border-[#0a0a0a]/20">Product category</th>
              {years.map((y) => (
                <th key={y} colSpan={weeks.filter((w) => w.year === y).length} className="text-center px-3 py-1.5 bm-eyebrow text-neutral-600 border-b border-[#0a0a0a]/20">
                  {y}
                </th>
              ))}
            </tr>
            <tr className="bg-[#ede8dc]">
              {weeks.map((w) => (
                <th key={w.start} className="text-right px-2 sm:px-4 py-2 bm-eyebrow text-neutral-700 border-b border-[#0a0a0a]/30 whitespace-nowrap">
                  {fmtWeek(w.start)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doc.categories.rows.length === 0 && (
              <tr><td colSpan={weeks.length + 1} className="px-4 py-4 bm-eyebrow text-neutral-500">No categories cleared 20 units in any of the three weeks.</td></tr>
            )}
            {doc.categories.rows.map((r, i) => {
              const hasKids = (r.children || []).length > 0;
              const isOpen = !!open[r.category];
              return (
                <Fragment key={r.category}>
                  <tr
                    onClick={hasKids ? () => toggle(r.category) : undefined}
                    className={`${i % 2 ? "bg-black/[0.03]" : ""} ${hasKids ? "cursor-pointer hover:bg-[#0a0a0a]/[0.06]" : ""}`}
                    data-testid={`category-row-${r.category}`}
                  >
                    <td className="text-left px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-medium">
                      <span className="flex items-center gap-1.5">
                        {hasKids ? (
                          <ChevronRight
                            size={13}
                            className={`shrink-0 text-[#c72c2c] transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
                            data-testid={`category-expand-${r.category}`}
                          />
                        ) : (
                          <span className="inline-block w-[13px] shrink-0" />
                        )}
                        {r.category}
                      </span>
                    </td>
                    {r.values.map((v, j) => (
                      <td key={j} className="text-right px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-mono-bm tabular-nums">
                        {v.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  {hasKids && isOpen && r.children.map((c) => (
                    <tr key={`${r.category}-${c.category}`} className="bg-[#0a0a0a]/[0.03]" data-testid={`subcategory-row-${c.category}`}>
                      <td className="text-left py-2 text-xs sm:text-sm text-neutral-700">
                        <span className="flex items-center gap-1.5 pl-7 sm:pl-10 border-l-2 border-[#c72c2c]/40 ml-3 sm:ml-5">
                          {c.category}
                        </span>
                      </td>
                      {c.values.map((v, j) => (
                        <td key={j} className="text-right px-2 sm:px-4 py-2 text-xs sm:text-sm font-mono-bm tabular-nums text-neutral-700">
                          {v.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className="bm-eyebrow text-neutral-400">
        Weeks run Monday–Sunday (Toronto), labelled by the Monday. Revenue and units are net of refunds; gift-card sales are excluded. Categories appear only if they cleared {">"}20 units in at least one week, and are ranked by the most recent week's revenue.
      </p>
    </div>
  );
}
